import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import { FOCUS_RING, RING_SURFACE } from '@/Components/Marketing/focusRing';
import formatDay from '@/Components/Marketing/formatDay';
import SmartLink from '@/Components/Marketing/SmartLink';
import useNearViewport from '@/Components/Marketing/useNearViewport';
import { BATTERY_CATEGORIES, REQUIREMENT_STATUSES, batteryRequirements } from '@/Pages/Landing/BatteryComplianceCalendar/requirements';
import { keyFamily } from '@/lib/keyFamily';
import { cn } from '@/lib/utils';

/** How many obligations the preview lists for a battery type. */
export const PREVIEW_ROWS = 4;

/** The battery type it opens on. */
export const DEFAULT_CATEGORY = 'ev';

/** A requirement's status code (hyphenated) as its key segment (camelCase). */
export const STATUS_KEY = { 'in-force': 'inForce', upcoming: 'upcoming', 'pending-act': 'pendingAct', 'in-testing': 'inTesting' };

/**
 * The calendar's battery types and statuses by name, translated as the
 * calendar page is. They are declared here, the lighter of the two modules
 * that read them: the calendar's data (Calendar/data, which re-exports them)
 * carries the English cards, which this preview keeps out of the pages that
 * show it.
 */
export const CATEGORY_COPY = keyFamily('site.calendar.category.{0}', BATTERY_CATEGORIES.map((c) => [c]));

export const STATUS_COPY = keyFamily('site.calendar.status.{0}', REQUIREMENT_STATUSES.map((s) => [STATUS_KEY[s]]));

/** A day in the visitor's own time zone as YYYY-MM-DD, as the calendar page compares dates. */
export function localIsoDate(date = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');

    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/**
 * What comes next for one battery type: the calendar's dated obligations
 * strictly after `today` (the calendar page's own rule for "next"), the
 * earliest `limit` of them grouped by date, in requirements.js's order
 * within a date. The last group says how many more share its date beyond
 * the limit (`more`), so a cut never hides that a date holds more.
 */
export function upcomingFor(category, today, limit = PREVIEW_ROWS) {
    const ahead = batteryRequirements
        .filter((req) => req.categories.includes(category) && req.status !== 'in-force' && req.date && req.date > today)
        .sort((a, b) => a.date.localeCompare(b.date));
    const groups = [];

    ahead.slice(0, limit).forEach((req) => {
        const last = groups[groups.length - 1];
        if (last?.date === req.date) last.items.push(req);
        else groups.push({ date: req.date, items: [req], more: 0 });
    });

    const last = groups[groups.length - 1];
    if (last) last.more = ahead.slice(limit).filter((req) => req.date === last.date).length;

    return groups;
}

// The calendar's per-locale content modules hold every card's full text
// (160 to 260 KB each); the preview needs only titles. So the active
// locale's module is fetched once the preview nears the viewport, never as
// part of the page that shows it (Home's first files stay as they were),
// once per session, English filling any card a translation lacks.
const CONTENT = {
    en: () => import('@/Pages/Landing/BatteryComplianceCalendar/content/en'),
    es: () => import('@/Pages/Landing/BatteryComplianceCalendar/content/es'),
    bg: () => import('@/Pages/Landing/BatteryComplianceCalendar/content/bg'),
};

const loaded = new Map();

function loadContent(locale) {
    const key = CONTENT[locale] ? locale : 'en';

    if (!loaded.has(key)) {
        loaded.set(
            key,
            CONTENT[key]().then(
                (module) => module.default,
                (error) => {
                    loaded.delete(key);
                    throw error;
                },
            ),
        );
    }

    return loaded.get(key);
}

/** Every requirement's title in `locale`, by id, English where the locale has none. */
export async function loadTitles(locale) {
    const active = await loadContent(locale);
    const english = batteryRequirements.every(({ id }) => active?.[id]?.requirement) ? active : await loadContent('en');

    return Object.fromEntries(batteryRequirements.map(({ id }) => [id, active?.[id]?.requirement || english?.[id]?.requirement]));
}

/** The titles, once `enabled` (near the viewport); the last ones loaded while another locale's arrive. */
function useTitles(locale, enabled) {
    const [titles, setTitles] = useState(null);

    useEffect(() => {
        if (!enabled) return undefined;
        let current = true;

        loadTitles(locale).then(
            (loadedTitles) => {
                if (current) setTitles(loadedTitles);
            },
            (error) => window.reportError?.(error),
        );

        return () => {
            current = false;
        };
    }, [locale, enabled]);

    return titles;
}

// As the calendar's own filter buttons are drawn (Calendar/Filters), a size
// smaller: the chosen type ink, the others white on a hairline.
const CHIP = {
    base: cn(
        'inline-flex h-8 items-center rounded-lg border px-3 text-sm font-medium',
        'transition-[background-color,border-color,color] duration-300 ease-mk motion-reduce:transition-none',
        FOCUS_RING,
    ),
    selected: 'border-mk-ink bg-mk-ink text-white shadow-[inset_0_1px_theme(colors.white/15%),0_1px_2px_theme(colors.mk.ink/15%)]',
    rest: 'border-mk-ink/10 bg-mk-surface text-mk-body shadow-[0_1px_2px_theme(colors.mk.ink/6%)] fine:hover:bg-mk-sunken fine:hover:text-mk-ink',
};

// The rail's geometry: each node's dot sits 5px down its row, 11px across;
// the segment under a dot runs to the next dot, across the 20px between rows.
const NODE = 'relative pl-7';
const SEGMENT = 'after:absolute after:left-[5px] after:top-[19px] after:-bottom-[22px] after:w-px after:bg-mk-line';
const DOT = 'absolute left-0 top-[5px] size-[11px] rounded-full border-2';

/**
 * A small, live piece of the compliance calendar: pick a battery type (the
 * calendar's own five, as WAI-ARIA tabs) and see its next obligations on a
 * short rail from today, by date, the next date marked. Each obligation
 * opens on the calendar itself (#<id>, which the calendar page opens as
 * that requirement's card); the calendar reads no battery type from its
 * address, so the type stays here. Dates and types come from
 * requirements.js, titles from the calendar's content (loaded near the
 * viewport, a placeholder bar until then). Light only: every page that
 * shows it shows it on a light surface.
 *
 * `framed` draws it as its own white card; without it, it sits bare inside
 * a card the caller draws (Home's resources hub).
 */
export default function CalendarPreview({ defaultCategory = DEFAULT_CATEGORY, framed = true, className }) {
    const { locale, formatMessage, formatDateToParts } = useIntl();
    const t = (id, values) => formatMessage({ id }, values);
    const baseId = useId();
    const tabRefs = useRef({});
    const [category, setCategory] = useState(BATTERY_CATEGORIES.includes(defaultCategory) ? defaultCategory : DEFAULT_CATEGORY);
    const [ref, near] = useNearViewport();
    const titles = useTitles(locale, near);

    // One clock per mount, as on the calendar page.
    const today = useMemo(() => localIsoDate(), []);
    const groups = useMemo(() => upcomingFor(category, today), [category, today]);

    const tabId = (code) => `${baseId}-tab-${code}`;
    const panelId = `${baseId}-panel`;
    const labelId = `${baseId}-label`;
    const categoryLabel = (code) => t(CATEGORY_COPY.key(code));
    const calendarHref = route('outer.batterycompliancecalendar');

    const choose = (code) => {
        setCategory(code);
        tabRefs.current[code]?.focus();
    };

    // Arrow keys move along the tabs (round the ends), Home and End to the ends; each choice shows at once.
    const handleKeyDown = (event) => {
        const index = BATTERY_CATEGORIES.indexOf(category);
        const last = BATTERY_CATEGORIES.length - 1;
        const next = {
            ArrowRight: index === last ? 0 : index + 1,
            ArrowLeft: index === 0 ? last : index - 1,
            Home: 0,
            End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        choose(BATTERY_CATEGORIES[next]);
    };

    return (
        <div
            ref={ref}
            data-calendar-preview=""
            className={cn(framed && `rounded-mk-card bg-mk-surface p-5 shadow-mk-soft ring-1 ring-mk-line sm:p-6 ${RING_SURFACE.surface}`, className)}
        >
            <p id={labelId} className="text-sm font-medium text-mk-ink">
                {t('site.calendar.filters.batteryType')}
            </p>
            <div role="tablist" aria-labelledby={labelId} className="mt-3 flex flex-wrap gap-1.5">
                {BATTERY_CATEGORIES.map((code) => {
                    const selected = code === category;

                    return (
                        <button
                            key={code}
                            ref={(element) => {
                                tabRefs.current[code] = element;
                            }}
                            type="button"
                            role="tab"
                            id={tabId(code)}
                            aria-selected={selected}
                            aria-controls={panelId}
                            tabIndex={selected ? 0 : -1}
                            onClick={() => setCategory(code)}
                            onKeyDown={handleKeyDown}
                            className={cn(CHIP.base, selected ? CHIP.selected : CHIP.rest)}
                        >
                            {categoryLabel(code)}
                        </button>
                    );
                })}
            </div>

            {/* Held at the tallest type's height where the card is widest (LMT's,
                measured at 448px), so switching types does not move the page. */}
            <div role="tabpanel" id={panelId} aria-labelledby={tabId(category)} className="mt-5 min-h-[22.5rem] border-t border-mk-line pt-5">
                <p className={cn(NODE, groups.length > 0 && SEGMENT, 'text-sm text-mk-subtle')}>
                    <span aria-hidden="true" className={cn(DOT, 'border-mk-subtle/40 bg-mk-sunken')} />
                    {t('site.calendar.timeline.today')}
                    {', '}
                    <time dateTime={today} className="tabular-nums">
                        {formatDay(formatDateToParts, today)}
                    </time>
                </p>

                {groups.length === 0 ? (
                    <p className="mt-5 text-[15px] leading-relaxed text-mk-body">{t('site.resources.calendarPreview.none')}</p>
                ) : (
                    <ol
                        role="list"
                        aria-label={t('site.resources.calendarPreview.list', { type: categoryLabel(category) })}
                        aria-busy={titles ? undefined : true}
                        className="mt-5 flex flex-col gap-5"
                    >
                        {groups.map((group, index) => {
                            const isNext = index === 0;

                            return (
                                <li
                                    key={group.date}
                                    data-obligation-date={group.date}
                                    data-next={isNext ? '' : undefined}
                                    className={cn(NODE, index < groups.length - 1 && SEGMENT)}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={cn(DOT, isNext ? 'border-mk-orange bg-mk-orange ring-4 ring-mk-orange/15' : 'border-mk-ink/25 bg-mk-surface')}
                                    />
                                    <p className="flex min-h-[21px] flex-wrap items-center gap-x-2 text-sm leading-5">
                                        <time dateTime={group.date} className={cn('tabular-nums text-mk-ink', isNext ? 'font-semibold' : 'font-medium')}>
                                            {formatDay(formatDateToParts, group.date)}
                                        </time>
                                        {isNext && (
                                            <span className="rounded-full bg-mk-orange-soft px-2 py-0.5 text-xs font-medium text-mk-orange-label">
                                                {t('site.daterail.next')}
                                            </span>
                                        )}
                                    </p>
                                    <ul role="list" className="-mx-2 mt-1.5 flex flex-col">
                                        {group.items.map((req) => (
                                            <li key={req.id} data-obligation={req.id}>
                                                <SmartLink
                                                    href={`${calendarHref}#${req.id}`}
                                                    className={cn(
                                                        'flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded-lg px-2 py-1.5',
                                                        'transition-colors duration-300 ease-mk hover:bg-mk-sunken motion-reduce:transition-none',
                                                        FOCUS_RING,
                                                    )}
                                                >
                                                    {titles ? (
                                                        <span className="text-[15px] font-medium leading-snug text-mk-ink">{titles[req.id]}</span>
                                                    ) : (
                                                        <span aria-hidden="true" data-title-placeholder="" className="my-1 block h-3.5 w-44 max-w-full rounded bg-mk-ink/[0.06]" />
                                                    )}
                                                    <span className="text-sm text-mk-subtle">{t(STATUS_COPY.key(STATUS_KEY[req.status]))}</span>
                                                </SmartLink>
                                            </li>
                                        ))}
                                    </ul>
                                    {group.more > 0 && (
                                        <p className="mt-1 text-sm text-mk-subtle">{t('site.calendar.timeline.moreCount', { count: group.more })}</p>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </div>
        </div>
    );
}
